import {
  Button,
  CardDescription,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiCellSignalSlash, PiRuler } from 'react-icons/pi';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useDrawingContext,
  useDrawingLifecycle,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { Feature, MapBrowserEvent } from 'ol';
import { cn } from '@ais-ui/utils';
import { parseSecondsToCustomDuration } from '../../utils/formatDuration';
import { Geometry, LineString } from 'ol/geom';
import { useLocalization } from '@ais-ui/locale-management';
import { noop } from 'lodash';
import { useDateTimeFormat } from '../../conversions/useDateFormat';

import { transform } from 'ol/proj';
import { getDistance } from 'ol/sphere';
import { Coordinate } from 'ol/coordinate';
import { useShipSelection } from './useShipSelection';
import { useLengthUnits } from '../../Units/useLengthUnits'; // Import the ship selection hook

// Custom hook for map click handling
function useMapClickHandler(enabled: boolean) {
  const [clickPosition, setClickPosition] = useState<Coordinate | null>(null);
  const { map } = useOpenLayersContext();

  useEffect(() => {
    const onClick = (e: MapBrowserEvent<UIEvent>) => {
      if (enabled) {
        setClickPosition(e.coordinate);
      }
    };

    if (enabled) {
      map.on('click', onClick);
    } else {
      map.un('click', onClick);
    }

    return () => {
      map.un('click', onClick);
    };
  }, [enabled, map]);

  return { clickPosition, setClickPosition };
}

interface MeasurementsProps {
  drawnFeature: Feature<Geometry> | null;
  onClose: () => void;
}

const Measurements = ({ drawnFeature, onClose }: MeasurementsProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const [enableClick, setEnableClick] = useState(true);
  const { clickPosition } = useMapClickHandler(enableClick);

  // Use our ship selection hook
  const {
    shipData,
    selectShipAtCoordinate,
    resetSelection,
    isSelecting,
    updateSpeed,
  } = useShipSelection();

  const { convertLength } = useLengthUnits();

  // Handle ship selection when clicked
  useEffect(() => {
    if (!clickPosition || !enableClick) {
      return;
    }

    selectShipAtCoordinate(clickPosition).then(() => {
      setEnableClick(false); // Disable further clicking after selection
    });
  }, [clickPosition, enableClick, selectShipAtCoordinate]);

  const [segmentLengths, setSegmentLengths] = useState<number[]>([]);
  const { messages } = useLocalization();

  const [duration, setDuration] = useState<string>('');
  const [arrival, setArrival] = useState<number | string>('');

  useEffect(() => {
    // Function to calculate lengths of segments
    const calculateSegmentLengths = (feature: Feature) => {
      if (feature && feature.getGeometry()) {
        const geometry = feature.getGeometry() as LineString;
        const coordinates = geometry.getCoordinates();
        const lengths: number[] = [];

        for (let i = 1; i < coordinates.length; i++) {
          const coord1_4326 = transform(
            coordinates[i - 1],
            'EPSG:3857',
            'EPSG:4326'
          );
          const coord2_4326 = transform(
            coordinates[i],
            'EPSG:3857',
            'EPSG:4326'
          );
          const lengthInMeters = getDistance(coord1_4326, coord2_4326);
          lengths.push(lengthInMeters); // Store in meters, let the hook handle conversion
        }
        setSegmentLengths(lengths);
      }
    };

    if (drawnFeature && drawnFeature.getGeometry()) {
      const handleGeometryChange = () => {
        calculateSegmentLengths(drawnFeature as Feature);
      };

      const geometry = drawnFeature.getGeometry() as Geometry;

      geometry.on('change', handleGeometryChange);

      // Clean up the event listener when the component unmounts or the feature changes
      return () => {
        if (drawnFeature && geometry) {
          geometry.un('change', handleGeometryChange);
        }
      };
    } else {
      setSegmentLengths([]);
    }
  }, [drawnFeature]);

  // Calculate total length using useMemo
  const totalLength = useMemo(() => {
    const totalInMeters = segmentLengths.reduce(
      (sum, length) => sum + length,
      0
    );
    return convertLength(totalInMeters);
  }, [segmentLengths, convertLength]);

  // Create tooltip content for segments
  const segmentTooltipContent = useMemo(() => {
    if (segmentLengths.length <= 1) return null;

    return (
      <div className="space-y-1">
        <div className="text-xs font-medium">
          {messages.mapMenuMeasureSegmentsHeader}
        </div>
        <div className="space-y-0.5">
          {segmentLengths.map((lengthInMeters, index) => {
            const converted = convertLength(lengthInMeters);
            return (
              <div
                key={index}
                className="flex justify-between text-xs min-w-32"
              >
                <span className="text-muted-foreground">
                  Segment {index + 1}:
                </span>
                <span className="ml-4">
                  {converted.value} {converted.abbreviation}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [segmentLengths, convertLength, messages.mapMenuMeasureSegmentsHeader]);

  useEffect(() => {
    if (
      shipData.speedOverGroundInKnots === 0 ||
      typeof shipData.speedOverGroundInKnots !== 'number'
    ) {
      setDuration('');
      setArrival('');
      return;
    }

    const calculateTime = () => {
      // For nautical calculations, we need the distance in nautical miles
      const totalInMeters = segmentLengths.reduce(
        (sum, length) => sum + length,
        0
      );
      const totalInNauticalMiles = totalInMeters * 0.000539957; // Convert meters to nautical miles

      // Calculate the time in hours it takes to traverse the length
      const timeInHours =
        totalInNauticalMiles / (shipData.speedOverGroundInKnots as number);
      const readableDuration = parseSecondsToCustomDuration(timeInHours * 3600);

      return { timeInHours, readableDuration: readableDuration || '' };
    };

    const updateDurationAndArrival = () => {
      const { timeInHours, readableDuration } = calculateTime();
      setDuration(readableDuration);

      // Calculate the time of arrival by adding the duration to the current date and time
      const currentTime = new Date();
      const durationMilliseconds = timeInHours * 3600000; // Convert hours to milliseconds
      const arrivalTime = new Date(
        currentTime.getTime() + durationMilliseconds
      );
      setArrival(formatDateTimeWithTimeZone(arrivalTime));
    };

    updateDurationAndArrival();
  }, [
    shipData.speedOverGroundInKnots,
    segmentLengths,
    formatDateTimeWithTimeZone,
  ]);

  // Reset ship selection when starting new measurement
  useEffect(() => {
    if (segmentLengths.length === 0) {
      resetSelection();
      setEnableClick(true); // Re-enable clicking when measurement is cleared
    }
  }, [segmentLengths.length, resetSelection]);

  // Handle manual speed input
  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      updateSpeed('');
    } else {
      const numberValue = parseFloat(e.target.value);
      if (numberValue >= 0) {
        updateSpeed(numberValue);
      }
    }
  };

  return (
    <div className="px-3 py-2 min-w-72 w-full">
      {segmentLengths.length === 0 && (
        <>
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-medium">Measure</h3>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 absolute top-1 right-1"
              onClick={onClose}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
                className="h-3 w-3"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Button>
          </div>
          <CardDescription className="text-xs">
            {messages.mapMenuMeasureStartMeasure}
          </CardDescription>
        </>
      )}
      {segmentLengths.length > 0 && (
        <div className="space-y-1.5">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex justify-between items-center">
                  <div className="flex space-x-2 text-xs">
                    <div className="font-medium">
                      {messages.mapMenuMeasureSegmentsSum}
                    </div>
                    <div className="underline text-muted-foreground">
                      {totalLength.value} {totalLength.abbreviation}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 absolute top-1 right-1"
                    onClick={onClose}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                      className="h-3 w-3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                </div>
              </TooltipTrigger>
              {segmentTooltipContent && (
                <TooltipContent side="right" className="max-w-xs">
                  {segmentTooltipContent}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <Separator className="w-[98%]"></Separator>

          {/* Two-column layout for all ship/calculation info */}
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-2 text-xs">
            <span className="font-medium">Ship</span>
            <span className="text-muted-foreground truncate">
              {isSelecting ? 'Selecting...' : shipData.shipName}
            </span>

            {shipData.isAisReportOld && (
              <>
                <span className="font-medium">Last AIS report</span>
                <div className="w-full flex justify-between">
                  <span className="text-muted-foreground truncate">
                    {parseSecondsToCustomDuration(shipData.aisReportAge)}
                  </span>
                  <PiCellSignalSlash className="h-4 w-4 text-yellow-600" />
                </div>
              </>
            )}

            <span className="font-medium">SOG (kn)</span>
            <div className="flex">
              <Input
                className="w-16 h-6 text-xs"
                type="number"
                value={shipData.speedOverGroundInKnots}
                onChange={handleSpeedChange}
              />
            </div>

            {duration && (
              <>
                <span className="font-medium">Duration</span>
                <span className="text-muted-foreground">{duration}</span>
              </>
            )}

            {arrival && (
              <>
                <span className="font-medium">ETA</span>
                <span className="text-muted-foreground">{arrival}</span>
              </>
            )}
          </div>

          {enableClick && (
            <div className="text-xs text-muted-foreground pt-1">
              Click on a ship to get its speed for ETA calculation
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface MeasureToolProps {
  className?: string;
}

export const MeasureTool = React.memo(({ className }: MeasureToolProps) => {
  const [isMeasuring, setIsMeasuring] = useState(false);
  const {
    onDraw,
    onCancel,
    onFinish,
    drawnFeature,
    setValidateWhileDrawing,
    geometryType,
  } = useDrawingContext();
  const [reload, setReload] = useState(false);
  const { messages } = useLocalization();

  const { onStartDrawing, onCancelDrawing, onModifyDraw } =
    useDrawingLifecycle();

  const onDrawMeasure = useCallback(
    (feature: Feature) => {
      onModifyDraw(feature);
      setReload((prev) => !prev);
    },
    [onModifyDraw]
  );

  const onCancelDrawingMeasure = useCallback(() => {
    onCancelDrawing();
    setIsMeasuring(false);
  }, [onCancelDrawing]);

  const onStartDrawingMeasure = useCallback(() => {
    onDraw.current = onDrawMeasure;
    onCancel.current = onCancelDrawingMeasure;
    onFinish.current = onCancelDrawingMeasure;

    onStartDrawing('LineString', undefined, false);
    setValidateWhileDrawing(true);
  }, [
    onDraw,
    onDrawMeasure,
    onCancel,
    onCancelDrawingMeasure,
    onFinish,
    onStartDrawing,
    setValidateWhileDrawing,
  ]);

  const onMeasure = useCallback(() => {
    if (!isMeasuring) {
      onStartDrawingMeasure();
      setIsMeasuring(true);
    } else {
      onCancelDrawing();
      setIsMeasuring(false);
    }
  }, [isMeasuring, onStartDrawingMeasure, onCancelDrawing]);

  // Add escape key handler
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMeasuring) {
        onCancelDrawing();
        setIsMeasuring(false);
      }
    };

    if (isMeasuring) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMeasuring, onCancelDrawing]);

  const isDrawingNotMeasuring = React.useMemo(() => {
    return Boolean(!isMeasuring && geometryType);
  }, [isMeasuring, geometryType]);

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={isMeasuring} onOpenChange={noop}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                id="measure-tool-button"
                onClick={onMeasure}
                disabled={isDrawingNotMeasuring}
                variant="outline"
                size="icon"
                className={cn(
                  'flex items-center justify-center h-8 w-8 p-1 group/btn rounded-lg',
                  className
                )}
              >
                <PiRuler className="h-8 w-8 group-hover/btn:text-primary" />
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent>{messages.mapMenuMeasureTooltip}</TooltipContent>
          <PopoverContent className="px-2 py-3 w-72" side="right" align="end">
            <div className="flex items-center">
              <Measurements
                drawnFeature={drawnFeature.current}
                onClose={onMeasure}
              />
            </div>
          </PopoverContent>
        </Popover>
      </Tooltip>
    </TooltipProvider>
  );
});

MeasureTool.displayName = 'MeasureTool';
